import {
    FlowTaskMessage,
    getFirebase,
    resolveAttachedFileFingerprint
} from '@assets/firebase/FirebaseAdapater';
import {
    TaskDefinition,
    useStore,
    useStoreUI,
    useTasksStore,
    useClientSettingStore
} from '@assets/model/Store';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Panel } from '../../Panel/Panel';
import { Flex } from '../../UI/Flex/Flex';
import { TaskChatContainer } from '../TaskChatContainer/TaskChatContainer';
import * as style from './TaskChatPanel.module.less';
import { ModalPickFile } from '@assets/component/ModalPickFile/ModalPickFile';
import { onValue, ref } from 'firebase/database';
import { position } from 'html2canvas/dist/types/css/property-descriptors/position';
import { cleanFilenameForDisplay } from '@assets/utils/Utils';
interface TaskChatPanelProps {
    taskId: string;
    isLocked: boolean;
}

export const TaskChatPanel = observer((p: TaskChatPanelProps) => {
    const store = useStore();

    const { taskId, isLocked } = p;
    const reportId = store.selectedReportId;
    const task: TaskDefinition = useTasksStore().get_task_definition(taskId);
    const [inputText, setInputValue] = useState('');
    const [attachmentNextMessage, setAttachmentNextMessage] = useState<
        string[]
    >([]);

    const clientSettings = useClientSettingStore();
    const groupName =
        clientSettings.workstreamsGroups.find(g => g.task_ids.includes(taskId))
            ?.title ?? '';

    const ui = useStoreUI();
    const refInput = useRef<HTMLTextAreaElement>(null);
    //Both panel and container use this, makes sense to move it into a store\something
    const [chatContent, setChatContent] = useState(null);

    useEffect(
        function fetchTaskContent() {
            const path = `/flowtasks/history/${reportId}/${taskId}/results`;
            const fb = getFirebase();
            const r = ref(fb.db, path);
            const unsub = onValue(r, snapshot => {
                const data = snapshot.val();
                console.log('TaskChatPanel: fetched task chat data:', data);
                setChatContent(data);
            });

            return () => {
                unsub();
            };
        },
        [taskId, reportId]
    );

    // Auto-expand textarea as user types
    useEffect(() => {
        const textarea = refInput.current;
        if (textarea) {
            textarea.style.height = '50px'; // Reset to minimum height
            const scrollHeight = textarea.scrollHeight;
            textarea.style.height = Math.min(scrollHeight, 220) + 'px';
        }
    }, [inputText]);

    const onClickSend = () => {
        if (ui.chatWaitingForResponse) return;
        if (isLocked) return;

        var input = refInput.current.value;
        if (input.length == 0) {
            input = ' ';
        }

        const taskStateForCheck =
            store.flowTaskState?.[store.selectedReportId]?.[taskId];
        const isModuleComplete =
            taskStateForCheck?.state_vars?.IS_COMPLETE === '1';
        const hasAttachments = attachmentNextMessage.length > 0;

        // Text-only message on a completed module: route through chat_refine
        // so the flow edits a staging copy of the saved output instead of
        // re-running the whole pipeline from scratch. The user can keep
        // iterating via chat indefinitely; the canonical only changes when
        // they explicitly Accept the staging proposal in the bubble.
        if (
            isModuleComplete &&
            !isLastMessageHumanInTheLoop &&
            !hasAttachments
        ) {
            const fb = getFirebase();
            fb.flowtask_invoke_chat_refine(
                store.selectedReportId,
                taskId,
                input
            );
            setAttachmentNextMessage([]);
            setInputValue('');
            return;
        }

        console.log(
            'Sending message to flow_id:',
            task.flow_id,
            'message:',
            input
        );
        const fb = getFirebase();
        const reportId = store.selectedReportId;
        var messageToSend: FlowTaskMessage = {
            content: input
        };

        if (attachmentNextMessage.length > 0) {
            messageToSend.attachedFiles = attachmentNextMessage;
            // Resolve a content fingerprint per file so the comparator detector
            // can tell apart "same content re-attached" from "new content".
            // Falls back to filename when ragDocId isn't available yet.
            messageToSend.attachedFilesFingerprints = attachmentNextMessage.map(
                path =>
                    resolveAttachedFileFingerprint(
                        store.selectedReportId,
                        path,
                        store.allProjectFiles
                    )
            );
        }

        //Approve/reject messages (direct logic in flowise)
        //Need approve yes\no -> No is normal chat if its a human-in-loop
        //Next step is done with ChatMessageApproveReject component

        if (isLastMessageHumanInTheLoop) {
            messageToSend.approve = false;
        }
        fb.flowtask_add_prompt_job(reportId, taskId, messageToSend);
        setAttachmentNextMessage([]);
        setInputValue('');
    };

    const onUploadFile = () => {
        if (ui.chatWaitingForResponse) return;
        if (isLocked) return;

        if (attachmentNextMessage.length > 0) {
            setAttachmentNextMessage([]);
            return;
        }

        ui.modal = {
            title: 'Pick File',
            el: (
                <ModalPickFile
                    onSelectFile={(files: string[]) => {
                        setAttachmentNextMessage(files);
                        ui.modal = null;
                    }}
                />
            )
        };
    };

    const waitingForReply = (
        <i>
            <div
                className="spinner-border spinner-border-sm"
                role="status"
                style={{
                    animationDuration: '1.25s',
                    width: '20px',
                    height: '20px'
                }}
            ></div>{' '}
        </i>
    );

    var isLastMessageHumanInTheLoop = false;
    if (chatContent != null && chatContent.length > 0) {
        const lastMessage = chatContent[chatContent.length - 1] || null;

        if (lastMessage?.action != null) {
            isLastMessageHumanInTheLoop = true;
        }
    }

    const sendIcon = isLastMessageHumanInTheLoop
        ? 'bi-arrow-clockwise'
        : 'bi-send';

    const sendButtonContent = (
        <i
            className={`bi ${sendIcon}`}
            onClick={() => {
                onClickSend();
            }}
        >
            <span style={{ paddingLeft: '3px' }}>Send</span>
        </i>
    );

    const taskState = store.flowTaskState?.[reportId]?.[taskId];
    const isWaitingReply =
        taskState != null &&
        taskState?.status != 'completed' &&
        taskState?.status != 'failed';

    const isLockedStyle = isLocked ? style.lockedInput : '';

    const sendButton = (
        <Button className={`${style.sendButton} ${isLockedStyle}`}>
            {isWaitingReply ? waitingForReply : sendButtonContent}
        </Button>
    );

    const uploadStyle =
        attachmentNextMessage.length == 0
            ? style.uploadButton
            : style.cancelUploadButton;
    const uploadButton = (
        <Button
            className={`${uploadStyle} ${isLockedStyle}`}
            onClick={() => {
                onUploadFile();
            }}
        >
            {attachmentNextMessage.length > 0 ? (
                <i className="bi bi-x"></i>
            ) : (
                <i className="bi bi-paperclip"></i>
            )}
        </Button>
    );

    const taskTitle = (
        <div className={style.taskTitle}>
            {groupName && (
                <span className="taskSectionTitle">{groupName} / </span>
            )}
            <span className="taskNameTitle">{task.name}</span>
        </div>
    );

    const removeAttachment = (file: string) => {
        const newAttachments = attachmentNextMessage.filter(f => f !== file);
        setAttachmentNextMessage(newAttachments);
    };

    const makeAttachmentBubble = (file: string) => {
        let trimmedFilename = file.split('/').slice(-1)[0];
        trimmedFilename = cleanFilenameForDisplay(trimmedFilename);
        if (trimmedFilename.length > 20) {
            trimmedFilename = trimmedFilename.slice(0, 15) + '...';
        }

        return (
            <div
                key={file}
                className={style.attachmentBubble}
                onClick={() => {
                    removeAttachment(file);
                }}
            >
                <Flex>
                    <span>{trimmedFilename}</span>
                    <i className="bi bi-x" style={{ marginLeft: '5px' }}></i>
                </Flex>
            </div>
        );
    };
    return (
        <Panel fillCell={true} className={style.customShadow}>
            <Flex direction="column" fillCell={true}>
                {taskTitle}
                <div className={style.messages}>
                    <TaskChatContainer
                        showWaitingAnimation={isWaitingReply}
                        isWaitingReply={isWaitingReply}
                        reportId={store.selectedReportId}
                        taskId={taskId}
                    />
                </div>
                <Flex className={style.inputLineOffset}>
                    <div>
                        {attachmentNextMessage.length > 0 && (
                            <Flex
                                style={{
                                    position: 'absolute',
                                    right: 20,
                                    top: -35
                                }}
                            >
                                {attachmentNextMessage.map((file, index) =>
                                    makeAttachmentBubble(file)
                                )}
                            </Flex>
                        )}
                    </div>
                    <div className={style.input}>
                        <textarea
                            ref={refInput}
                            value={inputText}
                            placeholder="Enter a question"
                            autoFocus={true}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    onClickSend();
                                }
                            }}
                            onChange={e => {
                                setInputValue(e.currentTarget.value);
                            }}
                        ></textarea>
                    </div>
                    {uploadButton}
                    {sendButton}
                </Flex>
            </Flex>
        </Panel>
    );
});
